import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { CustomModal } from "@/components/CustomModal";
import { TonIcon } from "@/components/Icons";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { useCreateEntry } from "@roman/shared/hooks/soccer/useCreateEntry";
import type { RomanApiError } from "@roman/shared/lib/net/romanApi";
import type { CreateEntry } from "@roman/shared/types/CreateEntry";
import { getErrorMessage } from "@roman/shared/utils/error";
import { fromNano } from "@ton/ton";
import { useCallback } from "react";

interface SufficientModalProps {
  isOpen: boolean;
  onClose: () => void;
  balance: number;
  totalFee: number;
  entryDetails: CreateEntry;
  onSuccess: () => void;
}

export const SufficientModal = ({
  isOpen,
  onClose,
  balance,
  totalFee,
  entryDetails,
  onSuccess,
}: SufficientModalProps) => {
  const { openModal: openErrorModal } = useErrorModalContext();
  const handleEnterEntryError = useCallback(
    (error: RomanApiError) => {
      const message = getErrorMessage(error);
      openErrorModal("Error entering round", message);
    },
    [openErrorModal],
  );
  const { trigger: createEntry, isMutating: createEntryMutating } = useCreateEntry(handleEnterEntryError);
  const onClickBuyEntry = async () => {
    if (!entryDetails.detail.type || !entryDetails.detail.captainPlayerId) {
      openErrorModal("Error entering round", "Not enough information to enter round");
      return;
    }
    const entryTx: CreateEntry = {
      ...entryDetails,
      detail: {
        ...entryDetails.detail,
        captainPlayerId: entryDetails.detail.captainPlayerId,
        ...(entryDetails.detail.type === "0002" && { viceCaptainPlayerId: entryDetails.detail.viceCaptainPlayerId }),
      },
    };

    await createEntry({ entryTx });
    onClose();
    onSuccess();
  };
  return (
    <CustomModal isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col text-white text-center mt-5">
        <div className="flex flex-col gap-3.5 text-white justify-center items-center">
          <ModalRow>
            <p className="text-xs">Ton in possession</p>
            <div className="flex items-center gap-1">
              <TonIcon className="w-4 h-4" />
              <p>{fromNano(balance)}</p>
            </div>
          </ModalRow>
          <ModalRow>
            <p className="text-xs">Total Entry fee</p>
            <div className="flex items-center gap-1">
              <TonIcon className="w-4 h-4" />
              <p>{fromNano(totalFee)}</p>
            </div>
          </ModalRow>
          <div className="w-full border border-solid my-3" />
          <ModalRow>
            <p className="text-sm">Amount after payment</p>
            <div className="flex items-center gap-1">
              <TonIcon className="w-6 h-6" />
              <p className="text-[22px]">{fromNano(balance - totalFee)}</p>
            </div>
          </ModalRow>
          <div className="my-6">
            <p className="text-xs text-gray-200 text-left">
              Successfully initiated" does not mean that the payment was successful but indicates that the payment
              process has been initiated (it may fail). If any issues arise, please visit the{" "}
              <a className="text-icon" href="https://t.me/ThumbaSupportBot" target="_blank" rel="noreferrer">
                "@ThumbaSupportBot".
              </a>
            </p>
          </div>
        </div>
        <PrimaryButton
          className="w-full mt-[30px]"
          text="Buy"
          onClick={onClickBuyEntry}
          isLoading={createEntryMutating}
        />
      </div>
    </CustomModal>
  );
};

const ModalRow = ({ children }: { children: React.ReactNode }) => {
  return <div className="flex justify-between items-center w-10/12">{children}</div>;
};
